import React from 'react'
import { Box, Typography, TextField, Chip } from '@mui/material'
import { FiCheck } from 'react-icons/fi'
import { AcknowledgementPointConfig } from '@/types/subscriptionAgreement'
import { isInitialFilled } from './acknowledgementUtils'

interface AcknowledgementPointProps {
  point: AcknowledgementPointConfig
  value: string
  onChange: (pointId: string, value: string) => void
  // The initials already entered on other points in this agreement, if any.
  // Every point must be initialed the same way — one signer, one set of
  // initials — so a value that differs is flagged rather than silently accepted.
  expectedInitials?: string | null
}

const AcknowledgementPoint: React.FC<AcknowledgementPointProps> = ({ point, value, onChange, expectedInitials }) => {
  const filled = isInitialFilled(value)
  const mismatch = filled && !!expectedInitials && value !== expectedInitials

  const handleChange = (raw: string) => {
    const cleaned = raw.replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, 2)
    onChange(point.id, cleaned)
  }

  return (
    <Box
      sx={{
        bgcolor: 'white',
        border: '1px solid',
        borderColor: mismatch ? '#E4A3A3' : filled ? '#A9DCC0' : '#E6E9ED',
        boxShadow: mismatch
          ? '0 0 0 3px rgba(196,68,68,0.09)'
          : filled
            ? '0 0 0 3px rgba(87,179,104,0.09)'
            : '0 1px 2px rgba(24,32,44,0.05)',
        borderRadius: 2,
        p: { xs: 2, sm: 2.5 },
        mb: 1.5,
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: '15.5px', color: '#2C333D', mb: 0.75 }}>
        <Box component="span" sx={{ color: '#7A6FA1', fontWeight: 800, mr: 1 }}>
          {String(point.number).padStart(2, '0')}
        </Box>
        {point.title}
      </Typography>

      {point.body.map((paragraph, idx) => (
        <Typography key={idx} sx={{ fontSize: '14.5px', color: '#4B5563', mb: idx === point.body.length - 1 ? 1 : 1 }}>
          {paragraph}
        </Typography>
      ))}

      <Chip
        label={point.ref}
        size="small"
        sx={{
          bgcolor: '#F5F6F8',
          color: '#7C8794',
          fontSize: '12px',
          height: 22,
          borderRadius: '5px',
          mt: 0.5,
        }}
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mt: 2,
          pt: 1.75,
          borderTop: '1px solid #E6E9ED',
        }}
      >
        <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: '#4B5563' }}>Initials</Typography>
        <TextField
          value={value}
          onChange={e => handleChange(e.target.value)}
          placeholder="AB"
          inputProps={{
            maxLength: 2,
            'aria-label': `Initials for point ${point.number}`,
            style: {
              width: 60,
              padding: '9px 12px',
              fontWeight: 700,
              fontSize: '15px',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              textAlign: 'center',
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: mismatch ? '#FDF1F1' : filled ? '#EAF7F0' : 'white',
              borderRadius: '8px',
              '& fieldset': { borderColor: mismatch ? '#E4A3A3' : filled ? '#A9DCC0' : '#CFD6DE' },
            },
          }}
        />
        {mismatch ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#C44444', fontSize: '13px', fontWeight: 700 }}>
            Doesn't match your other initials ({expectedInitials})
          </Box>
        ) : (
          filled && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#1E7A4E', fontSize: '13px', fontWeight: 700 }}>
              <FiCheck size={14} /> Recorded
            </Box>
          )
        )}
      </Box>
    </Box>
  )
}

export default AcknowledgementPoint
